import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { NotificationService } from '@progress/kendo-angular-notification';
import { CloudWhatsappService } from '../../services/cloud-whatsapp.service';
import { WhatsAppIntegracao, WhatsAppTelefone } from '../config-whatsapp/whatsapp.interface';

interface StatusTemplateItem {
  id?: number;
  nome?: string;
  status?: string;
  tipoNotificacao?: string;
  categoria?: string;
  textoMensagem?: string;
  mensagem?: string;
}

@Component({
  selector: 'app-templates-whatsapp',
  templateUrl: './templates-whatsapp.component.html',
  styleUrls: [
    '../config-whatsapp/config-whatsapp.component.scss',
    './templates-whatsapp.component.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TemplatesWhatsappComponent implements OnInit, OnDestroy {
  carregando = false;
  carregandoTemplates = false;
  criandoTemplates = false;
  sincronizandoTemplates = false;
  sincronizandoDoFacebook = false;
  modalEnviarTesteTemplateAberto = false;
  templateTesteSelecionado: StatusTemplateItem | null = null;
  numeroAutorizadoTesteTemplate = '';
  buscaTemplates = '';
  enviandoTesteTemplate = false;
  enviandoTesteTemplateId: number | null = null;

  dadosIntegracao: WhatsAppIntegracao = {
    associado: false,
    telefones: []
  };

  telefones: WhatsAppTelefone[] = [];

  statusTemplates: {
    configurado: boolean;
    modo: 'unica' | 'por_status' | null;
    enviarTemplateForaJanela: boolean;
    enviarTemplateMarketing: boolean;
    templates: StatusTemplateItem[];
  } = {
    configurado: false,
    modo: null,
    enviarTemplateForaJanela: true,
    enviarTemplateMarketing: true,
    templates: []
  };

  private destroy$ = new Subject<void>();

  constructor(
    private cloudWhatsapp: CloudWhatsappService,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.inicializar();
    this.setupNavigationListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async inicializar(): Promise<void> {
    await this.carregueIntegracao();

    if (this.dadosIntegracao.associado) {
      await this.carregueStatusTemplates();
    }
  }

  private setupNavigationListener(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        if (event.urlAfterRedirects.includes('/cloud-whatsapp/templates') && this.dadosIntegracao.associado) {
          this.carregueStatusTemplates();
        }
      });
  }

  private async carregueIntegracao(): Promise<void> {
    try {
      this.carregando = true;
      this.cdr.markForCheck();

      const dados = await this.cloudWhatsapp.carregueDados();

      this.dadosIntegracao = dados;
      this.telefones = (dados.telefones || []).map(telefone => {
        if (telefone.dataCriacao) {
          telefone.dataCriacao = new Date(telefone.dataCriacao);
        }
        return telefone;
      });
    } catch (erro) {
      console.error('[Templates WhatsApp] Erro ao carregar integração:', erro);
      this.dadosIntegracao = {
        associado: false,
        telefones: []
      };
      this.telefones = [];
    } finally {
      this.carregando = false;
      this.cdr.markForCheck();
    }
  }

  public async carregueStatusTemplates(): Promise<void> {
    if (!this.dadosIntegracao.associado) {
      return;
    }

    try {
      this.carregandoTemplates = true;
      this.cdr.markForCheck();

      const response = await this.cloudWhatsapp.obtenhaStatusTemplates();

      if (response) {
        this.statusTemplates = {
          configurado: response.configurado || false,
          modo: response.modo || null,
          enviarTemplateForaJanela: response.enviarTemplateForaJanela !== false,
          enviarTemplateMarketing: response.enviarTemplateMarketing !== false,
          templates: this.deduplicarTemplates(response.templates || [])
        };
      }
    } catch (erro) {
      console.error('[Templates WhatsApp] Erro ao carregar status dos templates:', erro);
    } finally {
      this.carregandoTemplates = false;
      this.cdr.markForCheck();
    }
  }

  public async crieTemplates(): Promise<void> {
    if (this.criandoTemplates) {
      return;
    }

    try {
      this.criandoTemplates = true;
      this.cdr.markForCheck();

      const response = await this.cloudWhatsapp.crieTemplatesNotificacao();

      if (response?.sucesso) {
        this.notificationService.show({
          content: response?.mensagem || 'Templates criados com sucesso! Aguardando aprovação da Meta.',
          cssClass: 'success',
          animation: { type: 'slide', duration: 400 },
          position: { horizontal: 'right', vertical: 'top' },
          type: { style: 'success', icon: true }
        });

        await this.carregueStatusTemplates();
        return;
      }

      throw new Error(response?.mensagem || 'Erro ao criar templates.');
    } catch (erro: any) {
      console.error('[Templates WhatsApp] Erro ao criar templates:', erro);
      this.notificationService.show({
        content: erro?.message || 'Erro ao criar templates. Tente novamente.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.criandoTemplates = false;
      this.cdr.markForCheck();
    }
  }

  public async sincronizeStatusTemplates(): Promise<void> {
    if (this.sincronizandoTemplates) {
      return;
    }

    try {
      this.sincronizandoTemplates = true;
      this.cdr.markForCheck();

      await this.cloudWhatsapp.sincronizeStatusTemplates();

      this.notificationService.show({
        content: 'Status dos templates atualizado com sucesso!',
        cssClass: 'success',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'success', icon: true }
      });

      await this.carregueStatusTemplates();
    } catch (erro: any) {
      console.error('[Templates WhatsApp] Erro ao sincronizar templates:', erro);
      this.notificationService.show({
        content: erro?.message || 'Erro ao sincronizar status. Tente novamente.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.sincronizandoTemplates = false;
      this.cdr.markForCheck();
    }
  }

  public async sincronizeDoFacebook(): Promise<void> {
    if (this.sincronizandoDoFacebook) {
      return;
    }

    try {
      this.sincronizandoDoFacebook = true;
      this.cdr.markForCheck();

      const response = await this.cloudWhatsapp.sincronizeTemplatesDoFacebook();

      this.notificationService.show({
        content: response?.mensagem || 'Templates sincronizados com sucesso!',
        cssClass: 'success',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'success', icon: true }
      });

      await this.carregueStatusTemplates();
    } catch (erro: any) {
      console.error('[Templates WhatsApp] Erro ao sincronizar templates do Facebook:', erro);
      this.notificationService.show({
        content: erro?.message || 'Erro ao sincronizar templates da Meta. Tente novamente.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.sincronizandoDoFacebook = false;
      this.cdr.markForCheck();
    }
  }

  public irParaConfiguracoes(): void {
    this.router.navigateByUrl('/admin/cloud-whatsapp/config');
  }

  public obtenhaDescricaoStatusTemplate(status: string): string {
    switch (status) {
      case 'APPROVED':
        return 'Aprovado';
      case 'PENDING':
        return 'Aguardando aprovação';
      case 'REJECTED':
        return 'Rejeitado';
      case 'NOT_CREATED':
        return 'Não criado';
      default:
        return status;
    }
  }

  public obtenhaClasseStatusTemplate(status: string): string {
    switch (status) {
      case 'APPROVED':
        return 'status-approved';
      case 'PENDING':
        return 'status-pending';
      case 'REJECTED':
        return 'status-rejected';
      case 'NOT_CREATED':
        return 'status-not-created';
      default:
        return '';
    }
  }

  public temTemplatesPendentes(): boolean {
    return this.statusTemplates.templates.some(template => template.status === 'PENDING');
  }

  public temTemplatesRejeitados(): boolean {
    return this.statusTemplates.templates.some(template => template.status === 'REJECTED');
  }

  public obtenhaDescricaoTipoNotificacao(tipo: string): string {
    const descricoes: { [key: string]: string } = {
      GanhouPontos: 'Ganhou Pontos',
      Aniversario: 'Aniversário',
      ResgatouCartao: 'Resgatou Cartão',
      SentimosSuaFalta: 'Sentimos Sua Falta',
      CartaoCompleto: 'Cartão Completo',
      ClientePerdido: 'Cliente Perdido',
      CarrinhoAbandonado: 'Carrinho Abandonado',
      NovoCartao: 'Novo Cartão',
      PontosExpirar: 'Pontos a Expirar',
      ConfirmarCartao: 'Confirmar Cartão',
      PedidoSaiuParaEntrega: 'Pedido Saiu para Entrega',
      PedidoEmPreparacao: 'Pedido em Preparação',
      PedidoPronto: 'Pedido Pronto',
      PedidoCancelado: 'Pedido Cancelado',
      PedidoEntregue: 'Pedido Entregue',
      PedidoRastreamentoUnico: 'Rastreamento de Pedido',
      PedidoConfirmado: 'Pedido Confirmado',
      SejaBemVindo: 'Seja Bem-Vindo',
      LinkExtratoCartao: 'Extrato do Cartão',
      PontosAtualizados: 'Pontos Atualizados'
    };

    return descricoes[tipo] || tipo || 'Não definido';
  }

  get telefonePrincipalTemplates(): WhatsAppTelefone | null {
    if (!this.telefones || this.telefones.length === 0) {
      return null;
    }

    return this.telefones.find(telefone => telefone.associado) || this.telefones[0] || null;
  }

  public abrirGerenciadorTemplatesMeta(): void {
    const url = new URL('https://business.facebook.com/wa/manage/message-templates/');
    const telefonePrincipal = this.telefonePrincipalTemplates;

    if (telefonePrincipal?.waba_id) {
      url.searchParams.set('asset_id', telefonePrincipal.asset_id || telefonePrincipal.waba_id);
      url.searchParams.set('waba_id', telefonePrincipal.waba_id);
    }

    if (telefonePrincipal?.business_id) {
      url.searchParams.set('business_id', telefonePrincipal.business_id);
    }

    window.open(url.toString(), '_blank');
  }

  get quantidadeTemplatesPendentes(): number {
    return this.statusTemplates.templates.filter(template => template.status === 'PENDING').length;
  }

  get templatesMarketing(): StatusTemplateItem[] {
    return this.filtrarTemplatesPorBusca(
      this.statusTemplates.templates.filter(template => (template.categoria || '').toUpperCase() === 'MARKETING')
    );
  }

  get templatesFluxoPedido(): StatusTemplateItem[] {
    return this.filtrarEOrdenarTemplates([
      'PedidoConfirmado',
      'PedidoEmPreparacao',
      'PedidoPronto',
      'PedidoSaiuParaEntrega',
      'PedidoEntregue',
      'PedidoCancelado'
    ]);
  }

  get templatesRastreamento(): StatusTemplateItem[] {
    return this.filtrarEOrdenarTemplates(['PedidoRastreamentoUnico']);
  }

  get templatesFidelidade(): StatusTemplateItem[] {
    return this.filtrarEOrdenarTemplates([
      'GanhouPontos',
      'NovoCartao',
      'SejaBemVindo',
      'ResgatouCartao',
      'CartaoCompleto',
      'ConfirmarCartao',
      'LinkExtratoCartao',
      'PontosAtualizados',
      'PontosExpirar'
    ]);
  }

  get templatesOutros(): StatusTemplateItem[] {
    const tiposAgrupados = new Set([
      ...this.templatesFluxoPedido.map(template => template.tipoNotificacao),
      ...this.templatesRastreamento.map(template => template.tipoNotificacao),
      ...this.templatesFidelidade.map(template => template.tipoNotificacao)
    ]);

    return this.filtrarTemplatesPorBusca(this.statusTemplates.templates.filter(template => {
      const categoriaMarketing = (template.categoria || '').toUpperCase() === 'MARKETING';
      return !categoriaMarketing && !tiposAgrupados.has(template.tipoNotificacao);
    }));
  }

  get totalTemplatesFiltrados(): number {
    return this.templatesFluxoPedido.length
      + this.templatesRastreamento.length
      + this.templatesFidelidade.length
      + this.templatesOutros.length
      + this.templatesMarketing.length;
  }

  private filtrarEOrdenarTemplates(tipos: string[]): StatusTemplateItem[] {
    const ordem = new Map(tipos.map((tipo, index) => [tipo, index]));

    return this.statusTemplates.templates
      .filter(template => tipos.includes(template.tipoNotificacao || ''))
      .filter(template => this.templateCorrespondeBusca(template))
      .sort((a, b) => {
        const ordemA = ordem.get(a.tipoNotificacao || '') ?? Number.MAX_SAFE_INTEGER;
        const ordemB = ordem.get(b.tipoNotificacao || '') ?? Number.MAX_SAFE_INTEGER;
        return ordemA - ordemB;
      });
  }

  private filtrarTemplatesPorBusca(templates: StatusTemplateItem[]): StatusTemplateItem[] {
    return templates.filter(template => this.templateCorrespondeBusca(template));
  }

  private templateCorrespondeBusca(template: StatusTemplateItem): boolean {
    const termo = this.normalizarBusca(this.buscaTemplates);
    if (!termo) {
      return true;
    }

    const conteudo = [
      template.nome,
      template.tipoNotificacao,
      template.categoria,
      template.status,
      template.textoMensagem,
      template.mensagem,
      this.obtenhaDescricaoTipoNotificacao(template.tipoNotificacao || ''),
      this.obtenhaDescricaoStatusTemplate(template.status || '')
    ]
      .filter(Boolean)
      .map(valor => this.normalizarBusca(String(valor)))
      .join(' ');

    return conteudo.includes(termo);
  }

  private normalizarBusca(valor: string): string {
    return (valor || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private deduplicarTemplates(templates: StatusTemplateItem[]): StatusTemplateItem[] {
    const mapa = new Map<string, StatusTemplateItem>();

    for (const template of templates || []) {
      const chave = (template?.tipoNotificacao || template?.nome || '').trim();
      if (!chave) {
        continue;
      }

      const atual = mapa.get(chave);
      if (!atual) {
        mapa.set(chave, template);
        continue;
      }

      if (this.deveSubstituirTemplateDuplicado(atual, template)) {
        mapa.set(chave, template);
      }
    }

    return Array.from(mapa.values());
  }

  private deveSubstituirTemplateDuplicado(atual: StatusTemplateItem, candidato: StatusTemplateItem): boolean {
    const prioridade = (status: string): number => {
      switch ((status || '').toUpperCase()) {
        case 'APPROVED':
          return 5;
        case 'PENDING':
        case 'PENDING_QUALITY_UPDATE':
          return 4;
        case 'PAUSED':
          return 3;
        case 'REJECTED':
          return 2;
        case 'NOT_CREATED':
          return 1;
        default:
          return 0;
      }
    };

    const prioridadeAtual = prioridade(atual?.status || '');
    const prioridadeCandidato = prioridade(candidato?.status || '');

    if (prioridadeCandidato !== prioridadeAtual) {
      return prioridadeCandidato > prioridadeAtual;
    }

    const atualTemTexto = !!(atual?.textoMensagem || atual?.mensagem);
    const candidatoTemTexto = !!(candidato?.textoMensagem || candidato?.mensagem);

    if (candidatoTemTexto !== atualTemTexto) {
      return candidatoTemTexto;
    }

    return !!candidato?.id && !atual?.id;
  }

  public abrirEditorTemplate(template: StatusTemplateItem): void {
    this.router.navigate(['/admin/editar-template-whatsapp', template.id]);
  }

  public ehNumeroDeTeste(): boolean {
    return this.telefones.some(telefone => {
      const nome = (telefone?.nome || '').toLowerCase();
      const numero = (telefone?.telefone || '').replace(/\s/g, '');
      return nome.includes('test') || numero.includes('555');
    });
  }

  public podeExibirBotaoTesteTemplate(template: StatusTemplateItem): boolean {
    return this.ehNumeroDeTeste() && !!template?.id;
  }

  public podeEnviarTesteTemplate(template: StatusTemplateItem): boolean {
    return this.podeExibirBotaoTesteTemplate(template) && template?.status === 'APPROVED';
  }

  public abrirModalEnviarTesteTemplate(template: StatusTemplateItem): void {
    if (!this.podeExibirBotaoTesteTemplate(template)) {
      return;
    }

    this.templateTesteSelecionado = template;
    this.numeroAutorizadoTesteTemplate = '';
    this.modalEnviarTesteTemplateAberto = true;
    this.cdr.markForCheck();
  }

  public fecharModalEnviarTesteTemplate(): void {
    if (this.enviandoTesteTemplate) {
      return;
    }

    this.modalEnviarTesteTemplateAberto = false;
    this.templateTesteSelecionado = null;
    this.numeroAutorizadoTesteTemplate = '';
    this.cdr.markForCheck();
  }

  public async enviarTesteTemplate(): Promise<void> {
    const template = this.templateTesteSelecionado;
    const numeroDestino = (this.numeroAutorizadoTesteTemplate || '').trim();
    let envioComSucesso = false;

    if (!template?.id) {
      this.notificationService.show({
        content: 'Template inválido para envio de teste.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
      return;
    }

    if (!numeroDestino) {
      this.notificationService.show({
        content: 'Informe um número autorizado para receber a mensagem de teste.',
        cssClass: 'warning',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'warning', icon: true }
      });
      return;
    }

    try {
      this.enviandoTesteTemplate = true;
      this.enviandoTesteTemplateId = template.id;
      this.cdr.markForCheck();

      const response = await this.cloudWhatsapp.envieTesteTemplate(template.id, numeroDestino);

      this.notificationService.show({
        content: response?.mensagem || 'Mensagem de teste enviada com sucesso!',
        cssClass: 'success',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'success', icon: true }
      });

      envioComSucesso = true;
    } catch (erro: any) {
      console.error('[Templates WhatsApp] Erro ao enviar teste:', erro);
      this.notificationService.show({
        content: erro?.message || 'Erro ao enviar mensagem de teste.',
        cssClass: 'error',
        animation: { type: 'slide', duration: 400 },
        position: { horizontal: 'right', vertical: 'top' },
        type: { style: 'error', icon: true }
      });
    } finally {
      this.enviandoTesteTemplate = false;
      this.enviandoTesteTemplateId = null;
      if (envioComSucesso) {
        this.fecharModalEnviarTesteTemplate();
      }
      this.cdr.markForCheck();
    }
  }
}
